import React, { useState } from "react";
import user from "../assets/images/order/delivary-partner.png";
import cancel2 from "../assets/images/navbar/cancel.png";
import { Order, OrderStatus } from "../utils/types";
import { restaurantOrderService } from "../services";
import { toast } from "react-toastify";

interface NewOrderModalProps {
  isOpen?: boolean;
  order: Order | null;
  onClose?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
}

const NewOrderModal: React.FC<NewOrderModalProps> = ({
  isOpen = true,
  order,
  onClose,
  onAccept,
  onReject,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !order) return null;

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      await restaurantOrderService.updateOrderStatus(
        order._id,
        OrderStatus.CONFIRMED
      );
      toast.success('Order accepted successfully!');
      onAccept?.();
    } catch (error: any) {
      console.error('Error accepting order:', error);
      toast.error(error?.message || 'Failed to accept order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsLoading(true);
      await restaurantOrderService.updateOrderStatus(
        order._id,
        OrderStatus.CANCELLED
      );
      toast.success('Order rejected');
      onReject?.();
    } catch (error: any) {
      console.error('Error rejecting order:', error);
      toast.error(error?.message || 'Failed to reject order');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate total items count
  const totalItems = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  
  // Format time
  const orderTime = new Date(order.createdAt).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-[550px] w-full p-6 transition-colors">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
            New Orders
          </h2>
          <button
            onClick={onClose}
            className="w-[36px] h-[36px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors border dark:border-gray-600 rounded-full"
          >
          <img src={cancel2} alt="Close" className="h-[18px] w-[18px]"/>
          </button>
        </div>

        {/* Order Count */}
        <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[14px] font-normal mb-5">
          {totalItems} Item{totalItems !== 1 ? 's' : ''} · Order #{order._id.slice(-6)}
        </p>

        {/* Customer Info Card */}
        <div className="border dark:border-gray-600 rounded-2xl p-4 mb-4 bg-white dark:bg-gray-700 transition-colors">
          <div className="flex items-start justify-between pb-3 border-b dark:border-gray-600">
            <div className="flex items-start gap-3">
              <img
                src={user}
                alt="Customer"
                className="w-12 h-12 rounded-[16px] object-cover"
              />
              <div>
                <h3 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-semibold mb-1">
                  {order.user?.fullName || order.deliveryAddress?.fullName || 'Customer'}
                </h3>
                <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal">
                  {order.deliveryAddress?.city || 'N/A'} · {orderTime}
                </p>
              </div>
            </div>
            <span className="text-[#FF8A00] font-Urbanist text-[16px] font-semibold">
              {order.orderStatus.toUpperCase()}
            </span>
          </div>

          {/* Order Items */}
          <div className="my-4 max-h-40 overflow-y-auto">
            {order.items?.map((item, index) => (
              <div key={item._id || index} className="flex items-center justify-between border-b border-dashed dark:border-gray-600 pb-3 mb-3">
                <span className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-normal">
                  {item.quantity} x {item.dishName}
                  {item.variantName && ` (${item.variantName})`}
                </span>
                <span className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-normal">
                  ₹{item.totalItemPrice.toFixed(2)}
                </span>
              </div>
            ))}
            {/* Total */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-semibold">
                Total
              </span>
              <span className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-semibold">
                ₹{order.totalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Tabs/Options */}
          <div className="flex items-center gap-3 mb-3 overflow-x-auto">
            <button className="px-3 py-1.5 bg-[#F5F5F5] dark:bg-gray-600 rounded-md border border-gray-200 dark:border-gray-500 text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-normal whitespace-nowrap">
              All Details
            </button>

            <div className="border dark:border-gray-600 rounded-[8px] bg-[#F5F5F5] dark:bg-gray-600 w-full">
              <button className="px-3 py-1.5 text-[#FF8A00] font-Urbanist text-[10px] font-normal whitespace-nowrap leading-[15px]">
                COOKING NOTE
              </button>
              <button className="px-3 py-1.5 text-[#6A6A6A] font-Urbanist text-[12px] font-normal whitespace-nowrap">
               “Please Add Extra Salt and Avoid garlic”
              </button>
            </div>
          </div>

          {/* Pagination Dots */}
          <div className="flex items-center justify-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#FF8A00]"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={isLoading}
            className="w-full bg-[#11C101] hover:bg-[#0d9600] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-Urbanist text-[16px] font-semibold py-3.5 rounded-full transition-colors"
          >
            {isLoading ? 'Processing...' : 'Accept'}
          </button>
          <button
            onClick={handleReject}
            disabled={isLoading}
            className="w-full bg-white border-2 border-gray-200 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-[#6A6A6A] font-Urbanist text-[16px] font-semibold py-3.5 rounded-full transition-colors"
          >
            {isLoading ? 'Processing...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrderModal;
